import React, { useState, useEffect } from 'react';
import AWSConnection from '../../../models/Connections/AWSConnection';
import { 
  Card, 
  CardContent, 
  TextField, 
  Switch, 
  FormControlLabel,
  Divider, 
  Typography,
  Box,
  IconButton,
  Collapse
} from '@mui/material';
import { Cloud } from 'lucide-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface AWSConnectionBoxProps {
  awsConnectionData: AWSConnection;
  onUpdate?: (connection: AWSConnection) => void;
}

const AWSConnectionBox: React.FC<AWSConnectionBoxProps> = ({ awsConnectionData, onUpdate }) => {
  const [data, setData] = useState<AWSConnection>(awsConnectionData);
  const [awsAccessKeyId, setAwsAccessKeyId] = useState<string>(data.connectionArgs.awsAccessKeyId || '');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState<string>(data.connectionArgs.awsSecretAccessKey || '');
  const [apigwConnectionId, setApigwConnectionId] = useState<string>(data.connectionArgs.apigwConnectionId || '');
  // Note: retrieve and learn properties are not part of AWSConnectionArgs
  // These were incorrectly added and should be removed
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  useEffect(() => {
    const updatedData = new AWSConnection({
      awsAccessKeyId,
      awsSecretAccessKey,
      apigwConnectionId
    });
    updatedData.id = awsConnectionData.id;
    setData(updatedData);

    if (onUpdate) {
      onUpdate(updatedData);
    }
  }, [awsAccessKeyId, awsSecretAccessKey, apigwConnectionId, awsConnectionData.id, onUpdate]);

  return (
    <Card variant="outlined" sx={{ borderColor: 'orange.300', borderWidth: 1 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="small"
            >
              {isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
            <Cloud size={20} color="#ff9800" />
            <Typography variant="h6" fontWeight="bold">
              AWS Cloud Connection
            </Typography>
          </Box>
        </Box>

        <Collapse in={!isCollapsed}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* AWS Configuration */}
            <TextField
              label="AWS Access Key ID"
              value={awsAccessKeyId}
              onChange={(e) => setAwsAccessKeyId(e.target.value)}
              placeholder="AKIA..."
              fullWidth
              size="small"
              helperText="Your AWS access key identifier"
            />

            <TextField
              label="AWS Secret Access Key"
              type="password"
              value={awsSecretAccessKey}
              onChange={(e) => setAwsSecretAccessKey(e.target.value)}
              placeholder="Enter secret key"
              fullWidth
              size="small"
              helperText="Your AWS secret access key (kept secure)"
            />

            <TextField
              label="API Gateway Connection ID"
              value={apigwConnectionId}
              onChange={(e) => setApigwConnectionId(e.target.value)}
              placeholder="connection-id"
              fullWidth
              size="small"
              helperText="API Gateway connection identifier"
            />
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default AWSConnectionBox;